import React, { useState, useEffect } from 'react';
import { useTitle } from '../../../context/TitleContext';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { motion } from 'framer-motion';
import { FaArrowLeft, FaImage, FaTag, FaBox, FaMoneyBillWave, FaWarehouse, FaChevronLeft, FaChevronRight, FaToggleOn, FaToggleOff } from 'react-icons/fa';
import axios from 'axios';

const BASE_URL = process.env.REACT_APP_BASE_URL;

const PharmacyProductDetails = () => {
    const { setTitle } = useTitle();
    const navigate = useNavigate();
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useEffect(() => {
        setTitle('تفاصيل المنتج');
        if (id) {
            fetchProductDetails();
        }
    }, [id, setTitle]);

    const getAuthToken = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.token;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const getUserRole = () => {
        const storeData = sessionStorage.getItem('storekhadamat');
        if (storeData) {
            try {
                const parsed = JSON.parse(storeData);
                return parsed.role;
            } catch (error) {
                return null;
            }
        }
        return null;
    };

    const fetchProductDetails = async () => {
        try {
            setLoading(true);
            const token = getAuthToken();
            const userRole = getUserRole();

            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                navigate('/pharmacy-products');
                return;
            }

            // تحديد endpoint حسب نوع المستخدم
            let endpoint;
            if (userRole === 'pharmacy_branch') {
                endpoint = `${BASE_URL}/pharmacy/branch/products/${id}`;
            } else {
                endpoint = `${BASE_URL}/pharmacy/products/${id}`;
            }

            const response = await axios.get(endpoint, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Accept-Language': 'ar',
                    'lang': 'ar',
                },
            });

            if (response.data.status === 200) {
                setProduct(response.data.data);
            } else {
                throw new Error(response.data.message || 'فشل في جلب تفاصيل المنتج');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || error.message || 'فشل في جلب تفاصيل المنتج', { containerId: 'main-toast-container' });
            navigate('/pharmacy-products');
        } finally {
            setLoading(false);
        }
    };

    const nextImage = () => {
        if (product && product.images) {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === product.images.length - 1 ? 0 : prevIndex + 1
            );
        }
    };

    const prevImage = () => {
        if (product && product.images) {
            setCurrentImageIndex((prevIndex) =>
                prevIndex === 0 ? product.images.length - 1 : prevIndex - 1
            );
        }
    };

    // التحقق من وجود Google Drive في رابط الصورة
    const isGoogleDriveLink = (url) => {
        if (!url) return false;
        return url.toLowerCase().includes('google.com') || url.toLowerCase().includes('drive.google.com');
    };

    // تحويل رابط Google Drive إلى رابط embed
    const convertToGoogleDriveEmbed = (url) => {
        if (!url) return url;
        
        // البحث عن file ID في رابط Google Drive
        const fileIdMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
        if (fileIdMatch && fileIdMatch[1]) {
            const fileId = fileIdMatch[1];
            return `https://drive.google.com/file/d/${fileId}/preview`;
        }
        
        // إذا كان الرابط يحتوي على رابط Google Drive كامل، استخراجه
        const driveUrlMatch = url.match(/https?:\/\/drive\.google\.com\/file\/d\/[a-zA-Z0-9_-]+[\/\?]?/);
        if (driveUrlMatch) {
            const driveUrl = driveUrlMatch[0].replace(/\/view$/, '').replace(/\/$/, '');
            return `${driveUrl}/preview`;
        }
        
        return url;
    };


    const handleToggleUnitStatus = async (unitId, currentStatus) => {
        try {
            const token = getAuthToken();
            const userRole = getUserRole();
            
            if (!token) {
                toast.error('لا يوجد token للمصادقة', { containerId: 'main-toast-container' });
                return;
            }

            const newStatus = currentStatus === 1 ? 0 : 1;



            // تحديد endpoint حسب نوع المستخدم
            let endpoint;
            if (userRole === 'pharmacy_branch') {
                endpoint = `${BASE_URL}/pharmacy/branch/products/${unitId}/variant-status?status=${newStatus}&type=unit`;
            } else {
                endpoint = `${BASE_URL}/pharmacy/products/${id}/units/${unitId}/toggle-status`;
            }

            if (userRole === 'pharmacy_branch') {
                // لـ pharmacy_branch استخدام POST مع query parameters في URL
                await axios.post(endpoint, {}, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                        'Content-Type': 'application/json',
                    },
                });
            } else {
                // لـ pharmacy و store استخدام POST مع body
                await axios.post(endpoint, {
                    enable: newStatus
                }, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Accept-Language': 'ar',
                        'lang': 'ar',
                        'Content-Type': 'application/json',
                    },
                });
            }

            toast.success(newStatus === 1 ? 'تم تفعيل الوحدة بنجاح' : 'تم إلغاء تفعيل الوحدة بنجاح', { containerId: 'main-toast-container' });
            fetchProductDetails(); // إعادة تحميل تفاصيل المنتج
        } catch (error) {
            toast.error('فشل في تغيير حالة الوحدة', { containerId: 'main-toast-container' });
        }
    };

    if (loading) {
        return (
            <div className="main_content">
                <div className="dashboard-container">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">جاري التحميل...</span>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="main_content">
                <div className="dashboard-container content-wrapper">
                    <div className="alert alert-warning text-center">
                        <h4>المنتج غير موجود</h4>
                        <p>المنتج المطلوب غير موجود أو تم حذفه</p>
                        <button
                            className="btn btn-primary"
                            onClick={() => navigate('/pharmacy-products')}
                        >
                            العودة للمنتجات
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="main_content">

            <div className="dashboard-container content-wrapper">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn btn-outline-secondary d-flex align-items-center"
                        onClick={() => navigate('/pharmacy-products')}
                        style={{ borderRadius: '8px' }}>
                        <FaArrowLeft className="mx-2" />
                        العودة
                    </motion.button>
                    <div></div>
                </div>

                {/* Product Details Card */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="card shadow-sm"
                    style={{ borderRadius: '12px' }}>

                    <div className="card-body p-4">
                        {/* Product Images Slider */}
                        {product.images && product.images.length > 0 && (
                            <div className="mb-4">
                                <h5 className="mb-3 d-flex align-items-center">
                                    <FaImage className="mx-2" style={{ color: '#184194' }} />
                                    صور المنتج
                                </h5>
                                <div className="position-relative" style={{ maxWidth: '500px', margin: '0 auto' }}>
                                    <div style={{
                                        position: 'relative',
                                        borderRadius: '12px',
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                                        backgroundColor: '#f8f9fa'
                                    }}>
                                        {isGoogleDriveLink(product.images[currentImageIndex].url) ? (
                                            <iframe
                                                src={convertToGoogleDriveEmbed(product.images[currentImageIndex].url)}
                                                title={`Product ${currentImageIndex + 1}`}
                                                style={{
                                                    width: '100%',
                                                    height: '300px',
                                                    border: 'none',
                                                    display: 'block'
                                                }}
                                            />
                                        ) : (
                                        <img
                                            src={product.images[currentImageIndex].url}
                                            alt={`Product ${currentImageIndex + 1}`}
                                            className="img-fluid"
                                            style={{
                                                width: '100%',
                                                height: '300px',
                                                objectFit: 'cover',
                                                display: 'block'
                                            }}
                                        />
                                        )}

                                        {/* Navigation Buttons */}
                                        {product.images.length > 1 && (
                                            <>
                                                <button
                                                    onClick={prevImage}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        left: '10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '40px',
                                                        height: '40px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronLeft style={{ color: '#184194' }} />
                                                </button>
                                                <button
                                                    onClick={nextImage}
                                                    className="btn btn-light position-absolute"
                                                    style={{
                                                        right: '10px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        borderRadius: '50%',
                                                        width: '40px',
                                                        height: '40px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                        border: 'none'
                                                    }}
                                                >
                                                    <FaChevronRight style={{ color: '#184194' }} />
                                                </button>
                                            </>
                                        )}

                                        {/* Image Counter */}
                                        {product.images.length > 1 && (
                                            <div className="position-absolute" style={{
                                                bottom: '10px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: 'rgba(0,0,0,0.7)',
                                                color: 'white',
                                                padding: '4px 12px',
                                                borderRadius: '20px',
                                                fontSize: '14px'
                                            }}>
                                                {currentImageIndex + 1} / {product.images.length}
                                            </div>
                                        )}
                                    </div>

                                    {/* Thumbnails */}
                                    {product.images.length > 1 && (
                                        <div className="d-flex justify-content-center gap-2 mt-3">
                                            {product.images.map((image, index) => (
                                                <button
                                                    key={index}
                                                    onClick={() => setCurrentImageIndex(index)}
                                                    className={`btn p-0 ${index === currentImageIndex ? 'border-primary' : ''}`}
                                                    style={{
                                                        border: '2px solid transparent',
                                                        borderRadius: '8px',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <img
                                                        src={image.url}
                                                        alt={`Thumbnail ${index + 1}`}
                                                        style={{
                                                            width: '60px',
                                                            height: '60px',
                                                            objectFit: 'cover',
                                                            display: 'block'
                                                        }}
                                                    />
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Product Information */}
                        <div className="row">
                            {/* Product Name */}
                            <div className="col-12 mb-3">
                                <h5 className="mb-3 d-flex align-items-center">
                                    <FaBox className="mx-2" style={{ color: '#184194' }} />
                                    اسم المنتج
                                </h5>
                                <p className="fs-5 fw-medium" style={{ color: '#333' }}>
                                    {product.name || 'غير محدد'}
                                </p>
                            </div>

                            {/* Product Code */}
                            {product.code && (
                                <div className="col-12 mb-3">
                                    <h5 className="mb-3 d-flex align-items-center">
                                        <FaTag className="mx-2" style={{ color: '#184194' }} />
                                        كود المنتج
                                    </h5>
                                    <p className="fw-medium" style={{ color: '#333' }}>
                                        {product.code}
                                    </p>
                                </div>
                            )}

                            {/* Product Description */}
                            <div className="col-12 mb-3">
                                <h5 className="mb-3 d-flex align-items-center">
                                    <FaTag className="mx-2" style={{ color: '#184194' }} />
                                    الوصف
                                </h5>
                                <p className="text-muted" style={{ lineHeight: '1.6' }}>
                                    {product.description || 'لا يوجد وصف'}
                                </p>
                            </div>

                            {/* Sub Category */}
                            {product.subcategory && (
                                <div className="col-md-6 mb-3">
                                    <h5 className="mb-3 d-flex align-items-center">
                                        <FaTag className="mx-2" style={{ color: '#184194' }} />
                                        الفئة الفرعية
                                    </h5>
                                    <p className="fw-medium" style={{ color: '#333' }}>
                                        {typeof product.subcategory === 'string' 
                                            ? product.subcategory 
                                            : product.subcategory.name || product.subcategory.name_ar || 'غير محدد'}
                                    </p>
                                </div>
                            )}

                            {/* Product Units */}
                            {product.units && product.units.length > 0 && (
                                <div className="col-12 mb-3">
                                    <h5 className="mb-3 d-flex align-items-center">
                                        <FaMoneyBillWave className="mx-2" style={{ color: '#184194' }} />
                                        وحدات المنتج
                                    </h5>
                                    <div className="table-responsive">
                                        <table className="table table-striped">
                                            <thead style={{ backgroundColor: '#f8f9fa' }}>
                                                <tr>
                                                    <th>نوع الوحدة</th>
                                                    <th>اسم الوحدة</th>
                                                    <th>السعر</th>
                                                    <th>الكمية</th>
                                                    <th>الحالة</th>
                                                    <th>الإجراءات</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {product.units.map((unit, index) => {
                                                    return (
                                                        <tr key={unit.id || index}>
                                                            <td>{unit.unit_type || 'غير محدد'}</td>
                                                            <td>{unit.unit_name || 'غير محدد'}</td>
                                                            <td className="fw-bold" style={{ color: '#28a745' }}>
                                                                {unit.price ? `${unit.price} ج.م` : 'غير محدد'}
                                                            </td>
                                                            <td className="fw-medium">
                                                                {unit.quantity || '0'}
                                                            </td>
                                                            <td>
                                                                <span className={`badge ${unit.status === true || unit.status === 1 || unit.enable === 1 ? 'bg-success' : 'bg-secondary'}`} style={{ color: 'white' }}>
                                                                    {unit.status === true || unit.status === 1 || unit.enable === 1 ? 'مفعل' : 'غير مفعل'}
                                                                </span>
                                                            </td>
                                                            <td>
                                                                <button
                                                                    className="btn btn-sm btn-toggle"
                                                                    style={{
                                                                        backgroundColor: unit.status === true || unit.status === 1 || unit.enable === 1 ? '#dc3545' : '#28a745',
                                                                        borderColor: unit.status === true || unit.status === 1 || unit.enable === 1 ? '#dc3545' : '#28a745',
                                                                        color: 'white'
                                                                    }}
                                                                    title={unit.status === true || unit.status === 1 || unit.enable === 1 ? 'إلغاء التفعيل' : 'تفعيل'}
                                                                    onClick={() => {
                                                                        const currentStatus = (unit.status === true || unit.status === 1 || unit.enable === 1) ? 1 : 0;
                                                                        handleToggleUnitStatus(unit.id, currentStatus);
                                                                    }}
                                                                >
                                                                    {unit.status === true || unit.status === 1 || unit.enable === 1 ? (
                                                                        <>
                                                                            <FaToggleOff className="mx-1" />
                                                                            إلغاء التفعيل
                                                                        </>
                                                                    ) : (
                                                                        <>
                                                                            <FaToggleOn className="mx-1" />
                                                                            تفعيل
                                                                        </>
                                                                    )}
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}

                            {/* Additional Info */}
                            <div className="col-12">
                                <div className="row">
                                    <div className="col-md-4 mb-3">
                                        <h6 className="text-muted">حالة المنتج</h6>
                                        <p className="fw-medium">
                                            <span className={`badge ${product.status === true || product.status === 1 || product.status === '1' ? 'bg-success' : 'bg-danger'}`} style={{ color: 'white' }}>
                                                {product.status === true || product.status === 1 || product.status === '1' ? 'نشط' : 'غير نشط'}
                                            </span>
                                        </p>
                                    </div>
                                    <div className="col-md-4 mb-3">
                                        <h6 className="text-muted">نسبة الخصم</h6>
                                        <p className="fw-medium">
                                            {product.discount && product.discount > 0 ? (
                                                <span className="text-danger">
                                                    {product.discount}%
                                                </span>
                                            ) : (
                                                <span className="text-muted">لا يوجد خصم</span>
                                            )}
                                        </p>
                                    </div>
                                    {product.default_price && (
                                        <div className="col-md-4 mb-3">
                                            <h6 className="text-muted">السعر الافتراضي</h6>
                                            <p className="fw-medium text-success">
                                                {product.default_price} ج.م
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>
        </div>
    );
};

export default PharmacyProductDetails;
